import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Calendar, Clock, DollarSign, Users, AlertCircle, CheckCircle, FileText, Settings, Eye, MapPin, Save, Plus, Edit3, Trash2, X, Edit2, Square, Timer, Coffee, Briefcase } from 'lucide-react';
import axios from 'axios';

const TimesheetManagement = () => {
  const { user, token, canAccessManager, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('settings'); // Start with settings tab
  const [timesheetReports, setTimesheetReports] = useState(null);
  const [timesheets, setTimesheets] = useState([]); // For all timesheets
  const [employeeConfigs, setEmployeeConfigs] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('current_week');
  const [customDateRange, setCustomDateRange] = useState({
    start_date: '',
    end_date: ''
  });
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [authError, setAuthError] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [employees, setEmployees] = useState([]);

  // Hour Adjustments state
  const [timeEntries, setTimeEntries] = useState([]);
  const [adjustmentPeriod, setAdjustmentPeriod] = useState('current_week');
  const [adjustmentDateRange, setAdjustmentDateRange] = useState({
    start_date: '',
    end_date: ''
  });
  const [selectedEmployeeForAdjustment, setSelectedEmployeeForAdjustment] = useState('all');
  const [editingEntry, setEditingEntry] = useState(null);
  const [adjustmentForm, setAdjustmentForm] = useState({
    new_hours: '',
    reason: ''
  });

  // Manual Clock In/Out state
  const [showManualEntryModal, setShowManualEntryModal] = useState(false);
  const [manualEntryForm, setManualEntryForm] = useState({
    employee_id: '',
    date: '',
    clock_in_time: '',
    clock_out_time: '',
    reason: ''
  });

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTimeEntry, setSelectedTimeEntry] = useState(null);

  // Timesheet Configuration State
  const [config, setConfig] = useState({
    location_tracking_enabled: false,
    after_hours_cutoff_time: '18:00',
    auto_clock_out_enabled: true,
    auto_clockout_grace_minutes: 30,
    pay_period_type: 'biweekly',
    original_pay_period_start_date: ''
  });
  const [showSaveModal, setShowSaveModal] = useState(false);
  
  // Shift Presets State
  const [shiftPresets, setShiftPresets] = useState([]);
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null);
  const [presetForm, setPresetForm] = useState({
    name: '',
    start_time: '09:00',
    end_time: '17:00',
    description: ''
  });

  // Pay Period Settings State
  const [payPeriodSettings, setPayPeriodSettings] = useState([]);
  const [showPayPeriodModal, setShowPayPeriodModal] = useState(false);
  const [editingPayPeriod, setEditingPayPeriod] = useState(null);
  const [payPeriodForm, setPayPeriodForm] = useState({
    period_type: 'weekly',
    config_name: '',
    is_default: false,
    original_start_date: '',
    weekly_config: {
      start_day: 'monday',
      end_day: 'sunday',
      name: 'Monday to Sunday'
    },
    bi_weekly_config: {
      start_day: 'saturday',
      end_day: 'friday',
      name: 'Saturday to Friday'
    },
    monthly_config: {
      start_day: 1,
      end_day: -1,
      name: '1st to Last Day'
    }
  });

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");
  const themeColor = 'var(--theme-primary)'; // Website theme blue color

  const periodOptions = [
    { value: 'current_week', label: 'Current Week' },
    { value: 'last_week', label: 'Last Week' },
    { value: 'current_month', label: 'Current Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const tabs = [
    { 
      id: 'settings', 
      label: 'Timesheet Settings', 
      icon: Settings,
      color: themeColor
    },
    { 
      id: 'timesheets', 
      label: 'Timesheets', 
      icon: FileText,
      color: themeColor
    },
    { 
      id: 'adjustments', 
      label: 'Adjust Hours', 
      icon: Edit2,
      color: themeColor
    }
  ];

  // Authentication check - only managers and admins
  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (token && !user) {
      return;
    }

    if (user && canAccessManager()) {
      setAuthError(null);
      setPageLoading(false);
    } else if (user && !canAccessManager()) {
      setAuthError('Access denied. Manager or admin privileges required.');
      setPageLoading(false);
    } else if (!token) {
      setAuthError('Please log in with manager or admin privileges to access timesheet management.');
      setPageLoading(false);
    }
  }, [user, token, authLoading, canAccessManager]);

  // Fetch data when page loads
  useEffect(() => {
    if (!pageLoading && user && canAccessManager()) {
      fetchEmployeeConfigs();
      fetchEmployees();
      if (activeTab === 'settings') {
        fetchTimesheetReports();
        fetchConfiguration();
        fetchShiftPresets();
        fetchPayPeriodSettings();
      } else if (activeTab === 'timesheets') {
        fetchTimesheets();
      } else if (activeTab === 'adjustments') {
        fetchTimeEntries();
      }
    }
  }, [pageLoading, user, canAccessManager, activeTab, selectedPeriod, customDateRange, selectedEmployee, adjustmentPeriod, adjustmentDateRange, selectedEmployeeForAdjustment]);

  const calculateDateRange = (period) => {
    const now = new Date();
    let startDate, endDate;

    switch (period) {
      case 'current_week':
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay()); // Sunday
        startDate = startOfWeek.toISOString().split('T')[0];
        endDate = new Date(startOfWeek.getTime() + 6 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]; // Saturday
        break;
      
      case 'last_week':
        const lastWeekStart = new Date(now);
        lastWeekStart.setDate(now.getDate() - now.getDay() - 7);
        startDate = lastWeekStart.toISOString().split('T')[0];
        endDate = new Date(lastWeekStart.getTime() + 6 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        break;
      
      case 'current_month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
        break;
      
      case 'last_month':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0];
        endDate = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0];
        break;
      
      case 'custom':
        return customDateRange;
      
      default:
        return { start_date: '', end_date: '' };
    }

    return { start_date: startDate, end_date: endDate };
  };

  const fetchTimesheetReports = async () => {
    setLoading(true);
    try {
      const dateRange = calculateDateRange(selectedPeriod);
      if (!dateRange.start_date || !dateRange.end_date) {
        setMessage({ type: 'error', text: 'Please select a valid date range' });
        return;
      }

      const params = new URLSearchParams({
        start_date: dateRange.start_date,
        end_date: dateRange.end_date
      });

      if (selectedEmployee !== 'all') {
        params.append('user_id', selectedEmployee);
      }

      const response = await axios.get(`${API_BASE_URL}/api/timesheet/reports?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setTimesheetReports(response.data);
      setMessage({ type: '', text: '' });
    } catch (err) {
      console.error('Error fetching timesheet reports:', err);
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.detail || 'Failed to fetch timesheet reports' 
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeConfigs = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/employee-configs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setEmployeeConfigs(response.data);
    } catch (err) {
      console.error('Error fetching employee configs:', err);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/employees`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // Filter out owner and admin roles - only show actual employees
      const actualEmployees = response.data.filter(employee => 
        employee.role !== 'owner' && employee.role !== 'admin'
      );
      setEmployees(actualEmployees);
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  };

  const fetchTimesheets = async () => {
    try {
      setLoading(true);
      
      // Fetch all published timesheet posts (similar to MyDashboard logic)
      const response = await axios.get(`${API_BASE_URL}/api/ai-posts/published?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data) {
        const publishedPosts = response.data.posts || response.data;
        
        // Filter for timesheet posts
        const timesheetPosts = publishedPosts.filter(post => {
          const isTimesheetPost = post.platforms?.includes('time_sheet') || 
                                post.post_type === 'time_sheet' ||
                                post.agent_type === 'timesheet_agent' ||
                                (post.agent_name && post.agent_name.toLowerCase().includes('timesheet'));
          
          return isTimesheetPost;
        });

        // Sort by created/published date (newest first)
        const sortedTimesheets = timesheetPosts.sort((a, b) => {
          const dateA = new Date(a.published_at || a.created_at);
          const dateB = new Date(b.published_at || b.created_at);
          return dateB - dateA;
        });

        setTimesheets(sortedTimesheets);
        console.log('Fetched timesheets:', sortedTimesheets.length, 'items');
      }
    } catch (error) {
      console.error('Error fetching timesheets:', error);
      setMessage({ type: 'error', text: 'Failed to load timesheets' });
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeEntries = async () => {
    try {
      setLoading(true);
      
      // Calculate date range based on selected period
      const dateRange = adjustmentPeriod === 'custom' ? adjustmentDateRange : calculateDateRange(adjustmentPeriod);
      
      const params = new URLSearchParams({
        start_date: dateRange.start_date,
        end_date: dateRange.end_date
      });

      if (selectedEmployeeForAdjustment !== 'all') {
        params.append('user_id', selectedEmployeeForAdjustment);
      }

      const response = await axios.get(`${API_BASE_URL}/api/timesheet/all-entries?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setTimeEntries(response.data);
      setMessage({ type: '', text: '' });
    } catch (err) {
      console.error('Error fetching time entries:', err);
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.detail || 'Failed to fetch time entries' 
      });
    } finally {
      setLoading(false);
    }
  };

  // Configuration Management Functions
  const fetchConfiguration = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/timesheet-config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (err) {
      console.error('Error fetching timesheet configuration:', err);
      if (err.response?.status === 404) {
        setMessage({ 
          type: 'info', 
          text: 'No configuration found. Default settings will be created when you save.' 
        });
      } else {
        setMessage({ 
          type: 'error', 
          text: err.response?.data?.detail || 'Failed to fetch timesheet configuration' 
        });
      }
    }
  };

  const fetchShiftPresets = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/shift-presets`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setShiftPresets(response.data);
    } catch (err) {
      console.error('Error fetching shift presets:', err);
      // Don't show error for presets, it's not critical
    }
  };

  const fetchPayPeriodSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/pay-period-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPayPeriodSettings(response.data);
    } catch (err) {
      console.error('Error fetching pay period settings:', err);
    }
  };

  const handleSaveConfiguration = async () => {
    setLoading(true);
    setShowSaveModal(true);
    setMessage({ type: '', text: '' });

    try {
      // Validate configuration
      if (parseInt(config.auto_clockout_grace_minutes) < 0) {
        setMessage({ type: 'error', text: 'Auto clock-out grace period cannot be negative' });
        return;
      }

      if (!config.original_pay_period_start_date) {
        setMessage({ type: 'error', text: 'Original Pay Period Start Date is required for accurate timesheet calculations' });
        return;
      }

      const updateData = {
        location_tracking_enabled: config.location_tracking_enabled,
        after_hours_cutoff_time: config.after_hours_cutoff_time,
        auto_clock_out_enabled: config.auto_clock_out_enabled,
        auto_clockout_grace_minutes: parseInt(config.auto_clockout_grace_minutes),
        pay_period_type: config.pay_period_type,
        original_pay_period_start_date: config.original_pay_period_start_date || null
      };

      await axios.put(`${API_BASE_URL}/api/timesheet-config`, updateData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setMessage({ type: 'success', text: 'Timesheet configuration saved successfully!' });
      
      // Keep modal open for a brief moment to show success
      setTimeout(() => {
        setShowSaveModal(false);
      }, 1000);
      
    } catch (err) {
      console.error('Error saving timesheet configuration:', err);
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.detail || 'Failed to save timesheet configuration' 
      });
      setShowSaveModal(false);
    } finally {
      setLoading(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const handleConfigChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  // Shift Preset Management Functions
  const openPresetModal = (preset = null) => {
    if (preset) {
      setEditingPreset(preset);
      setPresetForm({
        name: preset.name,
        start_time: preset.start_time,
        end_time: preset.end_time,
        description: preset.description || ''
      });
    } else {
      setEditingPreset(null);
      setPresetForm({
        name: '',
        start_time: '09:00',
        end_time: '17:00',
        description: ''
      });
    }
    setShowPresetModal(true);
  };

  const closePresetModal = () => {
    setShowPresetModal(false);
    setEditingPreset(null);
    setPresetForm({
      name: '',
      start_time: '09:00',
      end_time: '17:00',
      description: ''
    });
  };

  const handleSavePreset = async () => {
    if (!presetForm.name.trim()) {
      setMessage({ type: 'error', text: 'Preset name is required' });
      return;
    }

    setLoading(true);
    try {
      if (editingPreset) {
        // Update existing preset
        await axios.put(`${API_BASE_URL}/api/shift-presets/${editingPreset.id}`, presetForm, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setMessage({ type: 'success', text: 'Shift preset updated successfully!' });
      } else {
        // Create new preset
        await axios.post(`${API_BASE_URL}/api/shift-presets`, presetForm, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setMessage({ type: 'success', text: 'Shift preset created successfully!' });
      }

      closePresetModal();
      fetchShiftPresets(); // Reload presets
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to save shift preset';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setLoading(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const handleDeletePreset = async (presetId, presetName) => {
    if (!window.confirm(`Are you sure you want to delete the "${presetName}" preset?`)) {
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/shift-presets/${presetId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setMessage({ type: 'success', text: 'Shift preset deleted successfully!' });
      fetchShiftPresets(); // Reload presets
    } catch (err) {
      setMessage({ 
        type: 'error', 
        text: 'Failed to delete shift preset' 
      });
    } finally {
      setLoading(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const calculateDuration = (startTime, endTime) => {
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    
    let duration = (endHour * 60 + endMin) - (startHour * 60 + startMin);
    if (duration < 0) duration += 24 * 60; // Handle overnight shifts
    
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    return `${hours}h${minutes > 0 ? ` ${minutes}m` : ''}`;
  };

  // Pay Period Settings Management Functions
  const openPayPeriodModal = (setting = null) => {
    if (setting) {
      setEditingPayPeriod(setting);
      setPayPeriodForm({
        period_type: setting.period_type,
        config_name: setting.config_name,
        is_default: setting.is_default,
        original_start_date: setting.original_start_date || '',
        weekly_config: setting.weekly_config || {
          start_day: 'monday',
          end_day: 'sunday',
          name: 'Monday to Sunday'
        },
        bi_weekly_config: setting.bi_weekly_config || {
          start_day: 'saturday',
          end_day: 'friday',
          name: 'Saturday to Friday'
        },
        monthly_config: setting.monthly_config || {
          start_day: 1,
          end_day: -1,
          name: '1st to Last Day'
        }
      });
    } else {
      setEditingPayPeriod(null);
      setPayPeriodForm({
        period_type: 'weekly',
        config_name: '',
        is_default: false,
        original_start_date: '',
        weekly_config: {
          start_day: 'monday',
          end_day: 'sunday',
          name: 'Monday to Sunday'
        },
        bi_weekly_config: {
          start_day: 'saturday',
          end_day: 'friday',
          name: 'Saturday to Friday'
        },
        monthly_config: {
          start_day: 1,
          end_day: -1,
          name: '1st to Last Day'
        }
      });
    }
    setShowPayPeriodModal(true);
  };

  const closePayPeriodModal = () => {
    setShowPayPeriodModal(false);
    setEditingPayPeriod(null);
  };

  const handlePayPeriodFormChange = (field, value) => {
    setPayPeriodForm(prev => ({ ...prev, [field]: value }));
  };

  const handlePayPeriodConfigChange = (configType, field, value) => {
    setPayPeriodForm(prev => ({
      ...prev,
      [configType]: {
        ...prev[configType],
        [field]: value
      }
    }));
  };

  const handleSavePayPeriod = async () => {
    if (!payPeriodForm.config_name.trim()) {
      setMessage({ type: 'error', text: 'Configuration name is required' });
      return;
    }

    setLoading(true);
    try {
      const payloadData = {
        period_type: payPeriodForm.period_type,
        config_name: payPeriodForm.config_name,
        is_default: payPeriodForm.is_default
      };

      // Add the appropriate config based on period type
      if (payPeriodForm.period_type === 'weekly') {
        payloadData.weekly_config = payPeriodForm.weekly_config;
      } else if (payPeriodForm.period_type === 'bi_weekly') {
        payloadData.bi_weekly_config = payPeriodForm.bi_weekly_config;
      } else if (payPeriodForm.period_type === 'monthly') {
        payloadData.monthly_config = payPeriodForm.monthly_config;
      }

      if (editingPayPeriod) {
        // Update existing setting
        await axios.put(`${API_BASE_URL}/api/pay-period-settings/${editingPayPeriod.id}`, payloadData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setMessage({ type: 'success', text: 'Pay period setting updated successfully!' });
      } else {
        // Create new setting
        await axios.post(`${API_BASE_URL}/api/pay-period-settings`, payloadData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setMessage({ type: 'success', text: 'Pay period setting created successfully!' });
      }

      closePayPeriodModal();
      fetchPayPeriodSettings(); // Reload settings
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to save pay period setting';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setLoading(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const editPayPeriodSetting = (setting) => {
    openPayPeriodModal(setting);
  };

  const deletePayPeriodSetting = async (settingId) => {
    const setting = payPeriodSettings.find(s => s.id === settingId);
    if (!window.confirm(`Are you sure you want to delete the "${setting?.config_name}" pay period setting?`)) {
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/pay-period-settings/${settingId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setMessage({ type: 'success', text: 'Pay period setting deleted successfully!' });
      fetchPayPeriodSettings(); // Reload settings
    } catch (err) {
      setMessage({ 
        type: 'error', 
        text: 'Failed to delete pay period setting' 
      });
    } finally {
      setLoading(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  // Hour Adjustment Functions
  const handleEditHours = (entry) => {
    const currentHours = calculateHours(entry.clock_in_time, entry.clock_out_time);
    setEditingEntry(entry);
    setAdjustmentForm({
      new_hours: currentHours.toString(),
      reason: ''
    });
  };

  const handleSaveAdjustment = async () => {
    if (!editingEntry || !adjustmentForm.new_hours || !adjustmentForm.reason) {
      setMessage({ type: 'error', text: 'Please fill in all fields' });
      return;
    }

    try {
      setLoading(true);
      const originalHours = calculateHours(editingEntry.clock_in_time, editingEntry.clock_out_time);
      const newHours = parseFloat(adjustmentForm.new_hours);

      await axios.post(`${API_BASE_URL}/api/timesheet/adjustments`, {
        time_entry_id: editingEntry.id,
        user_id: editingEntry.user_id,
        adjustment_type: 'manual_hours',
        original_value: originalHours.toString(),
        new_value: newHours.toString(),
        reason: adjustmentForm.reason
      }, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setMessage({ type: 'success', text: 'Hours adjusted successfully!' });
      setEditingEntry(null);
      setAdjustmentForm({ new_hours: '', reason: '' });
      fetchTimeEntries();
    } catch (err) {
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.detail || 'Failed to save adjustment' 
      });
    } finally {
      setLoading(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const cancelAdjustment = () => {
    setEditingEntry(null);
    setAdjustmentForm({ new_hours: '', reason: '' });
  };

  // Open delete confirmation modal
  const openDeleteModal = (entryId, employeeName) => {
    setSelectedTimeEntry({ id: entryId, employeeName: employeeName });
    setShowDeleteModal(true);
  };

  // Close delete modal
  const closeDeleteModal = () => {
    setSelectedTimeEntry(null);
    setShowDeleteModal(false);
  };

  // Delete time entry
  const handleDeleteTimeEntry = async () => {
    if (!selectedTimeEntry) return;

    try {
      setLoading(true);
      
      await axios.delete(`${API_BASE_URL}/api/timesheet/entry/${selectedTimeEntry.id}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setMessage({ type: 'success', text: 'Time entry deleted successfully!' });
      
      // Refresh the time entries list
      await fetchTimeEntries();
      
      // Close the modal
      closeDeleteModal();
      
    } catch (error) {
      console.error('Error deleting time entry:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to delete time entry' 
      });
    } finally {
      setLoading(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  // Handle manual clock in/out entry
  const handleManualEntry = async () => {
    if (!manualEntryForm.employee_id || !manualEntryForm.date || !manualEntryForm.clock_in_time || !manualEntryForm.reason) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' });
      return;
    }

    try {
      setLoading(true);
      
      // Create the time entry data
      const entryData = {
        user_id: manualEntryForm.employee_id,
        date: manualEntryForm.date,
        clock_in_time: `${manualEntryForm.date}T${manualEntryForm.clock_in_time}:00.000Z`,
        clock_out_time: manualEntryForm.clock_out_time ? `${manualEntryForm.date}T${manualEntryForm.clock_out_time}:00.000Z` : null,
        reason: manualEntryForm.reason,
        is_manual_entry: true
      };

      await axios.post(`${API_BASE_URL}/api/timesheet/manual-entry`, entryData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setMessage({ type: 'success', text: 'Manual time entry added successfully!' });
      setShowManualEntryModal(false);
      setManualEntryForm({
        employee_id: '',
        date: '',
        clock_in_time: '',
        clock_out_time: '',
        reason: ''
      });
      fetchTimeEntries();
    } catch (err) {
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.detail || 'Failed to add manual entry' 
      });
    } finally {
      setLoading(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const handleEditEmployee = (config) => {
    setEditingEmployee(config.user_id);
    setEditForm({
      hourly_rate: config.hourly_rate,
      overtime_rate: config.overtime_rate || '',
      is_active: config.is_active
    });
  };

  const handleSaveEmployee = async (userId) => {
    setLoading(true);
    try {
      const updateData = {
        hourly_rate: parseFloat(editForm.hourly_rate),
        is_active: editForm.is_active
      };

      if (editForm.overtime_rate) {
        updateData.overtime_rate = parseFloat(editForm.overtime_rate);
      }

      await axios.put(`${API_BASE_URL}/api/employee-configs/${userId}`, updateData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setMessage({ type: 'success', text: 'Employee configuration updated successfully!' });
      setEditingEmployee(null);
      fetchEmployeeConfigs();
    } catch (err) {
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.detail || 'Failed to update employee configuration' 
      });
    } finally {
      setLoading(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour12 = parseInt(hours) % 12 || 12;
    const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
    return `${hour12}:${minutes} ${ampm}`;
  };

  const calculateHours = (clockIn, clockOut) => {
    if (!clockIn || !clockOut) return 0;
    const start = new Date(clockIn);
    const end = new Date(clockOut);
    return Math.round(((end - start) / (1000 * 60 * 60)) * 100) / 100;
  };

  const formatHours = (hours) => {
    return hours.toFixed(2);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getEmployeeName = (userId) => {
    const employee = employees.find(emp => emp.id === userId);
    return employee ? employee.full_name : 'Unknown Employee';
  };

  const formatDateRange = (timesheet) => {
    // Check both potential locations for date info
    const startDate = timesheet.timesheet_data?.start_date || timesheet.agent_data?.start_date;
    const endDate = timesheet.timesheet_data?.end_date || timesheet.agent_data?.end_date;
    
    if (startDate && endDate) {
      const start = new Date(startDate + 'T00:00:00');
      const end = new Date(endDate + 'T00:00:00');
      return formatFriendlyDateRange(start, end);
    }
    
    // Try to extract from topic if available
    if (timesheet.topic) {
      const dateMatch = timesheet.topic.match(/(\w+ \d{1,2}, \d{4})/g);
      if (dateMatch && dateMatch.length >= 2) {
        return `${dateMatch[0]} to ${dateMatch[1]}`;
      }
    }
    
    return 'Date range not available';
  };

  const formatFriendlyDateRange = (startDate, endDate) => {
    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'
    ];
    
    const getOrdinalSuffix = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    const startMonth = monthNames[startDate.getMonth()];
    const startDay = startDate.getDate();
    const endMonth = monthNames[endDate.getMonth()];
    const endDay = endDate.getDate();
    
    const startFormatted = `${startMonth} ${startDay}${getOrdinalSuffix(startDay)}`;
    const endFormatted = `${endMonth} ${endDay}${getOrdinalSuffix(endDay)}`;
    
    return `${startFormatted} to ${endFormatted}`;
  };

  const handleViewTimesheet = (postId) => {
    window.open(`/timesheet-report/${postId}?from=timesheet-management&tab=timesheets`, '_blank');
  };

  // Loading state
  if (pageLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderBottomColor: themeColor }}></div>
          <p className="text-gray-600">Loading timesheet management...</p>
        </div>
      </div>
    );
  }

  // Show error if authentication failed
  if (authError) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-800 mb-2">Access Denied</h2>
            <p className="text-red-600">{authError}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-white border-b border-gray-200">
            <div className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Manage Timesheets
                  </h2>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock className="h-4 w-4" />
                  <span className="hidden sm:inline">Manager Dashboard</span>
                  <span className="sm:hidden">Dashboard</span>
                </div>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="p-4 sm:p-6">
            {/* Message Display */}
            {message.text && (
              <div className={`mb-6 rounded-md p-4 ${
                message.type === 'error' 
                  ? 'bg-red-50 border border-red-200' 
                  : 'bg-green-50 border border-green-200'
              }`}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    {message.type === 'error' ? (
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    ) : (
                      <CheckCircle className="h-5 w-5 text-green-400" />
                    )}
                  </div>
                  <div className="ml-3">
                    <p className={`text-sm font-medium ${
                      message.type === 'error' ? 'text-red-800' : 'text-green-800'
                    }`}>
                      {message.text}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Tab Navigation - Styled like Business Info */}
            <div className="bg-white rounded-lg shadow-sm mb-4 sm:mb-6 border border-gray-200">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                          isActive
                            ? 'border-theme-blue text-theme-blue'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span>{tab.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-3 sm:p-6">
                
                {/* Timesheet Settings Tab */}
                {activeTab === 'settings' && (
                  <div className="space-y-6">
                    {/* Location Tracking Section */}
                    <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                      <div className="flex items-center space-x-2 mb-4">
                        <MapPin className="h-5 w-5 text-theme-blue" />
                        <h3 className="text-base font-semibold text-gray-900">Location Tracking</h3>
                      </div>
                      
                      <div className="flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            id="location_tracking"
                            type="checkbox"
                            checked={config.location_tracking_enabled}
                            onChange={(e) => handleConfigChange('location_tracking_enabled', e.target.checked)}
                            className="focus:ring-blue-500 h-4 w-4 text-theme-blue border-gray-300 rounded"
                          />
                        </div>
                        <div className="ml-3">
                          <label htmlFor="location_tracking" className="font-medium text-sm text-gray-700">
                            Enable Location Tracking
                          </label>
                          <p className="text-xs text-gray-500 mt-1">
                            Record GPS coordinates and IP address during clock in/out to verify authorized locations.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* After Hours Settings Section */}
                    <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                      <div className="flex items-center space-x-2 mb-4">
                        <Clock className="h-5 w-5 text-purple-500" />
                        <h3 className="text-base font-semibold text-gray-900">After Hours Settings</h3>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            After Hours Cutoff Time
                          </label>
                          <input
                            type="time"
                            value={config.after_hours_cutoff_time}
                            onChange={(e) => handleConfigChange('after_hours_cutoff_time', e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Work after {formatTime(config.after_hours_cutoff_time)} will be paid at after hours rate
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            After Hours Pay Information
                          </label>
                          <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                            <p className="text-xs text-blue-800">
                              After hours rate is configured individually for each employee in their profile.
                              If not set, defaults to regular hourly rate.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Auto Clock-out Settings Section */}
                    <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                      <div className="flex items-center space-x-2 mb-4">
                        <Clock className="h-5 w-5 text-orange-500" />
                        <h3 className="text-base font-semibold text-gray-900">Auto Clock-out Settings</h3>
                      </div>
                      
                      <div className="space-y-4">
                        {/* Enable/Disable Auto Clock-out */}
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            id="auto_clock_out_enabled"
                            checked={config.auto_clock_out_enabled}
                            onChange={(e) => handleConfigChange('auto_clock_out_enabled', e.target.checked)}
                            className="w-4 h-4 text-theme-blue bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                          />
                          <label htmlFor="auto_clock_out_enabled" className="text-sm font-medium text-gray-700">
                            Enable Auto Clock-out
                          </label>
                        </div>
                        
                        {/* Grace Period - only show when enabled */}
                        {config.auto_clock_out_enabled && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Grace Period (minutes)
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="120"
                              value={config.auto_clockout_grace_minutes}
                              onChange={(e) => handleConfigChange('auto_clockout_grace_minutes', e.target.value)}
                              className="w-full md:w-48 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Employees will be automatically clocked out {config.auto_clockout_grace_minutes} minutes after business closing hours.
                            </p>
                          </div>
                        )}
                        
                        {/* Disabled State Message */}
                        {!config.auto_clock_out_enabled && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                            <div className="flex">
                              <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5 mr-2" />
                              <p className="text-xs text-yellow-700">
                                Auto clock-out is disabled. Employees will need to manually clock out at the end of their shifts.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Pay Period Foundation Date Section */}
                    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-lg p-5 shadow-sm">
                      <div className="flex items-center space-x-2 mb-4">
                        <Calendar className="h-5 w-5 text-purple-600" />
                        <h3 className="text-base font-semibold text-gray-900">Pay Period Foundation Date</h3>
                      </div>
                      
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Original Pay Period Start Date *
                          </label>
                          <input
                            type="date"
                            value={config.original_pay_period_start_date}
                            onChange={(e) => handleConfigChange('original_pay_period_start_date', e.target.value)}
                            className="w-full md:w-64 px-3 py-2 text-sm border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                          />
                        </div>
                        
                        <div className="px-3 py-2.5 bg-purple-100 border border-purple-200 rounded-md">
                          <div className="flex items-start space-x-2">
                            <AlertCircle className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-purple-800 mb-1">
                                Critical Foundation Setting
                              </p>
                              <p className="text-xs text-purple-700">
                                This date serves as the foundation for calculating ALL future pay periods and timesheets. 
                                Set this to the start date of your very first pay period.
                              </p>
                              <p className="text-xs text-purple-600 mt-1.5">
                                <strong>Example:</strong> If pay periods start on Mondays and your first Monday was January 1, 2024, enter 2024-01-01.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Custom Pay Period Settings Section */}
                    <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-5 w-5 text-purple-500" />
                          <h3 className="text-base font-semibold text-gray-900">Custom Pay Period Settings</h3>
                        </div>
                        <button
                          onClick={() => openPayPeriodModal()}
                          className="flex items-center space-x-2 px-3 py-1.5 text-sm text-white rounded-md transition-colors"
                          style={{ backgroundColor: '#10b981' }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
                        >
                          <Plus className="h-4 w-4" />
                          <span>Add Period</span>
                        </button>
                      </div>

                      {/* Pay Period Settings List */}
                      <div className="space-y-3">
                        {payPeriodSettings.length === 0 ? (
                          <div className="text-center py-6 text-gray-500">
                            <Calendar className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                            <p className="text-sm">No custom pay periods configured yet</p>
                            <p className="text-xs">Create your first pay period setting to get started</p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                            {payPeriodSettings.map((setting) => (
                              <div key={setting.id} className="bg-gray-50 border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow">
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-2 mb-1">
                                      <h4 className="font-medium text-sm text-gray-900">{setting.config_name}</h4>
                                      {setting.is_default && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                          Default
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-xs text-gray-600 space-y-1">
                                      <div className="flex items-center space-x-2">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                          setting.period_type === 'weekly' ? 'bg-blue-100 text-blue-800' :
                                          setting.period_type === 'bi_weekly' ? 'bg-yellow-100 text-yellow-800' :
                                          'bg-purple-100 text-purple-800'
                                        }`}>
                                          {setting.period_type === 'weekly' ? 'Weekly' : 
                                           setting.period_type === 'bi_weekly' ? 'Bi-Weekly' : 'Monthly'}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-1 ml-2">
                                    <button
                                      onClick={() => editPayPeriodSetting(setting)}
                                      className="p-1.5 text-gray-400 hover:text-theme-blue hover:bg-blue-50 rounded transition-colors"
                                      title="Edit"
                                    >
                                      <Edit3 className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                      onClick={() => deletePayPeriodSetting(setting.id)}
                                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                      title="Delete"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Shift Presets Section */}
                    <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-5 w-5 text-green-500" />
                          <h3 className="text-base font-semibold text-gray-900">Shift Presets</h3>
                        </div>
                        <button
                          onClick={() => openPresetModal()}
                          className="flex items-center space-x-2 px-3 py-1.5 text-sm text-white rounded-md transition-colors"
                          style={{ backgroundColor: '#10b981' }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
                        >
                          <Plus className="h-4 w-4" />
                          <span>Add Preset</span>
                        </button>
                      </div>

                      {/* Shift Presets List */}
                      <div className="space-y-3">
                        {shiftPresets.length === 0 ? (
                          <div className="text-center py-6 text-gray-500">
                            <Clock className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                            <p className="text-sm">No shift presets configured yet</p>
                            <p className="text-xs">Create your first shift preset to get started</p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {shiftPresets.map((preset) => (
                              <div key={preset.id} className="bg-gray-50 border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <h4 className="font-medium text-sm text-gray-900 mb-1">{preset.name}</h4>
                                    <div className="text-xs text-gray-600 space-y-1">
                                      <div className="flex items-center space-x-2">
                                        <Clock className="h-3 w-3" />
                                        <span>{formatTime(preset.start_time)} - {formatTime(preset.end_time)}</span>
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        Duration: {calculateDuration(preset.start_time, preset.end_time)}
                                      </div>
                                      {preset.description && (
                                        <p className="text-xs text-gray-500 mt-1.5">{preset.description}</p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-1 ml-2">
                                    <button
                                      onClick={() => openPresetModal(preset)}
                                      className="p-1.5 text-gray-400 hover:text-theme-blue hover:bg-blue-50 rounded transition-colors"
                                      title="Edit"
                                    >
                                      <Edit3 className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                      onClick={() => handleDeletePreset(preset.id, preset.name)}
                                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                      title="Delete"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Save Configuration Button */}
                    <div className="flex justify-end pt-2">
                      <button
                        onClick={handleSaveConfiguration}
                        disabled={loading}
                        className="flex items-center space-x-2 px-5 py-2.5 text-sm font-medium text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ backgroundColor: loading ? '#94a3b8' : '#10b981' }}
                        onMouseEnter={(e) => {
                          if (!loading) e.target.style.backgroundColor = '#059669';
                        }}
                        onMouseLeave={(e) => {
                          if (!loading) e.target.style.backgroundColor = '#10b981';
                        }}
                      >
                        <Save className="h-4 w-4" />
                        <span>{loading ? 'Saving...' : 'Save Configuration'}</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Timesheets Tab */}
                {activeTab === 'timesheets' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-medium text-gray-900">Published Timesheets</h3>
                    </div>

                    {loading ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="animate-spin h-8 w-8 border-4 border-theme-blue border-t-transparent rounded-full"></div>
                        <span className="ml-3 text-gray-600">Loading timesheets...</span>
                      </div>
                    ) : timesheets.length === 0 ? (
                      <div className="text-center py-12">
                        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Timesheets Found</h3>
                        <p className="text-gray-600 max-w-md mx-auto">
                          No published timesheets are available yet. Timesheets will appear here once they are generated and published.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {timesheets.map((timesheet) => {
                          // Get timesheet data from either location
                          const timesheetData = timesheet.timesheet_data || timesheet.agent_data || {};
                          const employeeData = timesheetData.employee_data || [];
                          const totalEmployees = employeeData.length;
                          const totalHours = employeeData.reduce((sum, emp) => sum + (emp.total_hours || 0), 0);
                          const totalPay = employeeData.reduce((sum, emp) => sum + ((emp.total_hours || 0) * (emp.hourly_rate || 0)), 0);
                          
                          return (
                            <div key={timesheet.id} className="bg-gray-50 rounded-lg p-6">
                              <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center space-x-3">
                                  <FileText className="h-6 w-6 text-green-500" />
                                  <div>
                                    <h4 className="text-base font-medium text-gray-900">
                                      {formatDateRange(timesheet)}
                                    </h4>
                                    <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                                      <span>Generated: {new Date(timesheet.created_at).toLocaleDateString()}</span>
                                      <span>Employees: {totalEmployees}</span>
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => handleViewTimesheet(timesheet.id)}
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  >
                                    <Eye className="h-4 w-4 mr-2" />
                                    View Detailed Report
                                  </button>
                                </div>
                              </div>
                              
                              {/* Summary Grid */}
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                                <div>
                                  <span className="block text-sm font-medium text-gray-600 mb-1">Total Hours:</span>
                                  <p className="text-2xl font-bold text-gray-900">{totalHours.toFixed(2)}h</p>
                                </div>
                                <div>
                                  <span className="block text-sm font-medium text-gray-600 mb-1">Total Pay:</span>
                                  <p className="text-2xl font-bold text-green-600">${totalPay.toFixed(2)}</p>
                                </div>
                                <div>
                                  <span className="block text-sm font-medium text-gray-600 mb-1">Employees:</span>
                                  <p className="text-2xl font-bold text-theme-blue">{totalEmployees}</p>
                                </div>
                              </div>
                              
                              {/* Employee Breakdown */}
                              {employeeData.length > 0 && (
                                <div className="mt-6">
                                  <details className="group">
                                    <summary className="flex items-center cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                                      <span>Employee Breakdown ({employeeData.length} employees)</span>
                                      <div className="ml-2 group-open:rotate-180 transition-transform">
                                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                      </div>
                                    </summary>
                                    
                                    <div className="mt-4 space-y-3">
                                      {employeeData.map((emp, index) => (
                                        <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
                                          <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center space-x-2">
                                              <Users className="h-4 w-4 text-gray-500" />
                                              <span className="font-medium text-gray-900">{emp.name}</span>
                                              <span className="text-sm text-gray-500">({emp.role})</span>
                                            </div>
                                          </div>
                                          
                                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                            <div>
                                              <span className="text-gray-600">Total Hours:</span>
                                              <p className="font-medium">{emp.total_hours?.toFixed(2) || '0.00'}h</p>
                                            </div>
                                            <div>
                                              <span className="text-gray-600">Regular Hours:</span>
                                              <p className="font-medium">{emp.regular_hours?.toFixed(2) || '0.00'}h</p>
                                            </div>
                                            <div>
                                              <span className="text-gray-600">After Hours:</span>
                                              <p className="font-medium">{emp.after_hours?.toFixed(2) || '0.00'}h</p>
                                            </div>
                                            <div>
                                              <span className="text-gray-600">Total Pay:</span>
                                              <p className="font-medium text-green-600">${((emp.total_hours || 0) * (emp.hourly_rate || 0)).toFixed(2)}</p>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </details>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Adjust Hours Tab */}
                {activeTab === 'adjustments' && (
                  <div className="space-y-6">
                    {/* Controls */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Period Selection */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Period</label>
                          <select
                            value={adjustmentPeriod}
                            onChange={(e) => setAdjustmentPeriod(e.target.value)}
                            className="w-full px-3 py-2 pr-16 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                            style={{ 
                              '--tw-ring-color': themeColor + '50',
                              focusRingColor: themeColor
                            }}
                          >
                            {periodOptions.map(option => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </div>

                        {/* Employee Selection */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Employee</label>
                          <select
                            value={selectedEmployeeForAdjustment}
                            onChange={(e) => setSelectedEmployeeForAdjustment(e.target.value)}
                            className="w-full px-3 py-2 pr-16 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                            style={{ 
                              '--tw-ring-color': themeColor + '50',
                              focusRingColor: themeColor
                            }}
                          >
                            <option value="all">All Employees</option>
                            {employees.map(employee => (
                              <option key={employee.id} value={employee.id}>{employee.full_name}</option>
                            ))}
                          </select>
                        </div>

                        {/* Custom Date Range */}
                        {adjustmentPeriod === 'custom' && (
                          <div className="md:col-span-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                                <input
                                  type="date"
                                  value={adjustmentDateRange.start_date}
                                  onChange={(e) => setAdjustmentDateRange(prev => ({...prev, start_date: e.target.value}))}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                                  style={{ 
                                    '--tw-ring-color': themeColor + '50',
                                    focusRingColor: themeColor
                                  }}
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                                <input
                                  type="date"
                                  value={adjustmentDateRange.end_date}
                                  onChange={(e) => setAdjustmentDateRange(prev => ({...prev, end_date: e.target.value}))}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                                  style={{ 
                                    '--tw-ring-color': themeColor + '50',
                                    focusRingColor: themeColor
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Time Entries Table */}
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-medium text-gray-900 flex items-center">
                              <Edit2 className="h-5 w-5 mr-2 text-yellow-600" />
                              Hour Adjustments
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">
                              Review and adjust employee hours for the selected period
                            </p>
                          </div>
                          <button
                            onClick={() => setShowManualEntryModal(true)}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2"
                            style={{ 
                              backgroundColor: themeColor,
                              focusRingColor: themeColor
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#1e9bb8'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = themeColor}
                          >
                            <Clock className="h-4 w-4 mr-2" />
                            Add Manual Entry
                          </button>
                        </div>
                      </div>

                      {loading ? (
                        <div className="flex items-center justify-center py-12">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderBottomColor: themeColor }}></div>
                          <span className="ml-2 text-gray-600">Loading time entries...</span>
                        </div>
                      ) : timeEntries.length === 0 ? (
                        <div className="text-center py-12">
                          <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                          <h4 className="text-lg font-medium text-gray-900 mb-2">No Time Entries Found</h4>
                          <p className="text-gray-600">
                            No time entries found for the selected period and employee filter.
                          </p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Employee
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Clock In
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Clock Out
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Hours Worked
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {timeEntries.map((entry) => {
                                const currentHours = calculateHours(entry.clock_in_time, entry.clock_out_time);
                                const isEditing = editingEntry?.id === entry.id;
                                
                                return (
                                  <React.Fragment key={entry.id}>
                                    <tr className={isEditing ? 'bg-yellow-50' : 'hover:bg-gray-50'}>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="font-medium text-gray-900">
                                          {getEmployeeName(entry.user_id)}
                                        </div>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                                        {formatDate(entry.clock_in_time)}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                                        {formatTime(entry.clock_in_time)}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                                        {entry.clock_out_time ? formatTime(entry.clock_out_time) : 'Not clocked out'}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        {isEditing ? (
                                          <input
                                            type="number"
                                            value={adjustmentForm.new_hours}
                                            onChange={(e) => setAdjustmentForm(prev => ({...prev, new_hours: e.target.value}))}
                                            step="0.25"
                                            className="w-20 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                                            style={{ 
                                              '--tw-ring-color': themeColor + '50',
                                              focusRingColor: themeColor
                                            }}
                                            placeholder="Hours"
                                          />
                                        ) : (
                                          <span className="font-medium text-gray-900">
                                            {currentHours.toFixed(2)} hrs
                                          </span>
                                        )}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="text-sm text-gray-600">
                                          {entry.status?.charAt(0).toUpperCase() + entry.status?.slice(1)}
                                        </span>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        {isEditing ? (
                                          <div className="flex space-x-2">
                                            <button
                                              onClick={handleSaveAdjustment}
                                              disabled={loading || !adjustmentForm.reason.trim()}
                                              className="text-green-600 hover:text-green-900 disabled:opacity-50"
                                              title={!adjustmentForm.reason.trim() ? 'Please provide a reason for the adjustment' : 'Save adjustment'}
                                            >
                                              <Save className="h-4 w-4" />
                                            </button>
                                            <button
                                              onClick={cancelAdjustment}
                                              className="text-gray-600 hover:text-gray-900"
                                            >
                                              <X className="h-4 w-4" />
                                            </button>
                                          </div>
                                        ) : (
                                          <div className="flex space-x-2">
                                            <button
                                              onClick={() => handleEditHours(entry)}
                                              disabled={!entry.clock_out_time}
                                              className="hover:opacity-75 disabled:opacity-50 disabled:cursor-not-allowed"
                                              style={{ color: themeColor }}
                                              title={!entry.clock_out_time ? 'Cannot adjust hours for active time entries' : 'Adjust hours'}
                                            >
                                              <Edit2 className="h-4 w-4" />
                                            </button>
                                            <button
                                              onClick={() => openDeleteModal(entry.id, getEmployeeName(entry.user_id))}
                                              className="text-red-600 hover:text-red-900"
                                              title="Delete time entry"
                                              disabled={loading}
                                            >
                                              <Trash2 className="h-4 w-4" />
                                            </button>
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                    {isEditing && (
                                      <tr className="bg-yellow-50">
                                        <td colSpan="7" className="px-6 py-4">
                                          <div className="max-w-md">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                              Reason for Adjustment *
                                            </label>
                                            <textarea
                                              value={adjustmentForm.reason}
                                              onChange={(e) => setAdjustmentForm(prev => ({...prev, reason: e.target.value}))}
                                              rows="2"
                                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                                              style={{ 
                                                '--tw-ring-color': themeColor + '50',
                                                focusRingColor: themeColor
                                              }}
                                              placeholder="Explain why this adjustment is needed..."
                                            />
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Shift Preset Modal */}
      {showPresetModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingPreset ? 'Edit Shift Preset' : 'Add Shift Preset'}
                </h3>
                <button
                  onClick={closePresetModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preset Name *
                  </label>
                  <input
                    type="text"
                    value={presetForm.name}
                    onChange={(e) => setPresetForm(prev => ({...prev, name: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                    style={{ 
                      '--tw-ring-color': themeColor + '50',
                      focusRingColor: themeColor
                    }}
                    placeholder="e.g., Morning Shift"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Time
                    </label>
                    <input
                      type="time"
                      value={presetForm.start_time}
                      onChange={(e) => setPresetForm(prev => ({...prev, start_time: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                      style={{ 
                        '--tw-ring-color': themeColor + '50',
                        focusRingColor: themeColor
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Time
                    </label>
                    <input
                      type="time"
                      value={presetForm.end_time}
                      onChange={(e) => setPresetForm(prev => ({...prev, end_time: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                      style={{ 
                        '--tw-ring-color': themeColor + '50',
                        focusRingColor: themeColor
                      }}
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description (Optional)
                  </label>
                  <textarea
                    value={presetForm.description}
                    onChange={(e) => setPresetForm(prev => ({...prev, description: e.target.value}))}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                    style={{ 
                      '--tw-ring-color': themeColor + '50',
                      focusRingColor: themeColor
                    }}
                    placeholder="Describe this shift preset..."
                  />
                </div>
                
                {presetForm.start_time && presetForm.end_time && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                    <p className="text-sm text-blue-800">
                      Duration: {calculateDuration(presetForm.start_time, presetForm.end_time)}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={closePresetModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePreset}
                  disabled={loading || !presetForm.name.trim()}
                  className="px-4 py-2 text-sm font-medium text-white rounded-md"
                  style={{ 
                    backgroundColor: loading || !presetForm.name.trim() ? '#9ca3af' : themeColor,
                    cursor: loading || !presetForm.name.trim() ? 'not-allowed' : 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    if (!loading && presetForm.name.trim()) {
                      e.target.style.backgroundColor = '#1e9bb8';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading && presetForm.name.trim()) {
                      e.target.style.backgroundColor = themeColor;
                    }
                  }}
                >
                  {loading ? 'Saving...' : editingPreset ? 'Update Preset' : 'Create Preset'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pay Period Setting Modal */}
      {showPayPeriodModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingPayPeriod ? 'Edit Pay Period Setting' : 'Add Pay Period Setting'}
                </h3>
                <button
                  onClick={closePayPeriodModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Configuration Name *
                    </label>
                    <input
                      type="text"
                      value={payPeriodForm.config_name}
                      onChange={(e) => handlePayPeriodFormChange('config_name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                      style={{ 
                        '--tw-ring-color': themeColor + '50',
                        focusRingColor: themeColor
                      }}
                      placeholder="e.g., Standard Bi-Weekly"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Period Type
                    </label>
                    <select
                      value={payPeriodForm.period_type}
                      onChange={(e) => handlePayPeriodFormChange('period_type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                      style={{ 
                        '--tw-ring-color': themeColor + '50',
                        focusRingColor: themeColor
                      }}
                    >
                      <option value="weekly">Weekly</option>
                      <option value="bi_weekly">Bi-Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="is_default"
                    checked={payPeriodForm.is_default}
                    onChange={(e) => handlePayPeriodFormChange('is_default', e.target.checked)}
                    className="w-4 h-4 text-theme-blue bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <label htmlFor="is_default" className="text-sm font-medium text-gray-700">
                    Set as default configuration
                  </label>
                </div>

                {/* Period Type Specific Config */}
                {payPeriodForm.period_type === 'weekly' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-3">Weekly Configuration</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-blue-800 mb-1">Start Day</label>
                        <select
                          value={payPeriodForm.weekly_config.start_day}
                          onChange={(e) => handlePayPeriodConfigChange('weekly_config', 'start_day', e.target.value)}
                          className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="monday">Monday</option>
                          <option value="tuesday">Tuesday</option>
                          <option value="wednesday">Wednesday</option>
                          <option value="thursday">Thursday</option>
                          <option value="friday">Friday</option>
                          <option value="saturday">Saturday</option>
                          <option value="sunday">Sunday</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-blue-800 mb-1">End Day</label>
                        <select
                          value={payPeriodForm.weekly_config.end_day}
                          onChange={(e) => handlePayPeriodConfigChange('weekly_config', 'end_day', e.target.value)}
                          className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="monday">Monday</option>
                          <option value="tuesday">Tuesday</option>
                          <option value="wednesday">Wednesday</option>
                          <option value="thursday">Thursday</option>
                          <option value="friday">Friday</option>
                          <option value="saturday">Saturday</option>
                          <option value="sunday">Sunday</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {payPeriodForm.period_type === 'bi_weekly' && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h4 className="font-medium text-yellow-900 mb-3">Bi-Weekly Configuration</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-yellow-800 mb-1">Start Day</label>
                        <select
                          value={payPeriodForm.bi_weekly_config.start_day}
                          onChange={(e) => handlePayPeriodConfigChange('bi_weekly_config', 'start_day', e.target.value)}
                          className="w-full px-3 py-2 border border-yellow-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        >
                          <option value="monday">Monday</option>
                          <option value="tuesday">Tuesday</option>
                          <option value="wednesday">Wednesday</option>
                          <option value="thursday">Thursday</option>
                          <option value="friday">Friday</option>
                          <option value="saturday">Saturday</option>
                          <option value="sunday">Sunday</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-yellow-800 mb-1">End Day</label>
                        <select
                          value={payPeriodForm.bi_weekly_config.end_day}
                          onChange={(e) => handlePayPeriodConfigChange('bi_weekly_config', 'end_day', e.target.value)}
                          className="w-full px-3 py-2 border border-yellow-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        >
                          <option value="monday">Monday</option>
                          <option value="tuesday">Tuesday</option>
                          <option value="wednesday">Wednesday</option>
                          <option value="thursday">Thursday</option>
                          <option value="friday">Friday</option>
                          <option value="saturday">Saturday</option>
                          <option value="sunday">Sunday</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {payPeriodForm.period_type === 'monthly' && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h4 className="font-medium text-purple-900 mb-3">Monthly Configuration</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-purple-800 mb-1">Start Day</label>
                        <select
                          value={payPeriodForm.monthly_config.start_day}
                          onChange={(e) => handlePayPeriodConfigChange('monthly_config', 'start_day', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          {Array.from({length: 28}, (_, i) => i + 1).map(day => (
                            <option key={day} value={day}>{day}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-purple-800 mb-1">End Day</label>
                        <select
                          value={payPeriodForm.monthly_config.end_day}
                          onChange={(e) => handlePayPeriodConfigChange('monthly_config', 'end_day', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value={-1}>Last Day of Month</option>
                          {Array.from({length: 28}, (_, i) => i + 1).map(day => (
                            <option key={day} value={day}>{day}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={closePayPeriodModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePayPeriod}
                  disabled={loading || !payPeriodForm.config_name.trim()}
                  className="px-4 py-2 text-sm font-medium text-white rounded-md"
                  style={{ 
                    backgroundColor: loading || !payPeriodForm.config_name.trim() ? '#9ca3af' : themeColor,
                    cursor: loading || !payPeriodForm.config_name.trim() ? 'not-allowed' : 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    if (!loading && payPeriodForm.config_name.trim()) {
                      e.target.style.backgroundColor = '#1e9bb8';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading && payPeriodForm.config_name.trim()) {
                      e.target.style.backgroundColor = themeColor;
                    }
                  }}
                >
                  {loading ? 'Saving...' : editingPayPeriod ? 'Update Setting' : 'Create Setting'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Save Configuration Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-sm shadow-lg rounded-md bg-white">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                <Save className="h-6 w-6 text-theme-blue" />
              </div>
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900">
                  Saving Configuration
                </h3>
                <div className="mt-2 px-7 py-3">
                  <div className="animate-spin h-8 w-8 border-4 border-theme-blue border-t-transparent rounded-full mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-2">
                    Please wait while we save your timesheet configuration...
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Adjustment Reason Modal */}
      {editingEntry && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md bg-white rounded-lg shadow-lg">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-900">Adjust Hours</h3>
              <p className="text-sm text-gray-600 mt-1">
                {getEmployeeName(editingEntry.user_id)} - {formatDate(editingEntry.clock_in_time)}
              </p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Original Hours: {calculateHours(editingEntry.clock_in_time, editingEntry.clock_out_time).toFixed(2)}
                </label>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Hours
                </label>
                <input
                  type="number"
                  value={adjustmentForm.new_hours}
                  onChange={(e) => setAdjustmentForm(prev => ({...prev, new_hours: e.target.value}))}
                  step="0.25"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                  style={{ 
                    '--tw-ring-color': themeColor + '50',
                    focusRingColor: themeColor
                  }}
                  placeholder="Enter new hours"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Adjustment *
                </label>
                <textarea
                  value={adjustmentForm.reason}
                  onChange={(e) => setAdjustmentForm(prev => ({...prev, reason: e.target.value}))}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                  style={{ 
                    '--tw-ring-color': themeColor + '50',
                    focusRingColor: themeColor
                  }}
                  placeholder="Explain why this adjustment is needed..."
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={cancelAdjustment}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAdjustment}
                disabled={loading || !adjustmentForm.new_hours || !adjustmentForm.reason}
                className="px-4 py-2 text-sm font-medium text-white rounded-md"
                style={{ 
                  backgroundColor: loading || !adjustmentForm.new_hours || !adjustmentForm.reason ? '#9ca3af' : themeColor,
                  cursor: loading || !adjustmentForm.new_hours || !adjustmentForm.reason ? 'not-allowed' : 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (!loading && adjustmentForm.new_hours && adjustmentForm.reason) {
                    e.target.style.backgroundColor = '#1e9bb8';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading && adjustmentForm.new_hours && adjustmentForm.reason) {
                    e.target.style.backgroundColor = themeColor;
                  }
                }}
              >
                {loading ? 'Saving...' : 'Save Adjustment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Clock Entry Modal */}
      {showManualEntryModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
            {/* Sticky Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" style={{ backgroundColor: 'var(--theme-primary)' }}>
              <h3 className="text-lg font-medium text-white flex items-center">
                <Clock className="h-5 w-5 mr-2 text-white" />
                Add Manual Time Entry
              </h3>
              <button
                onClick={() => setShowManualEntryModal(false)}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-4">
                {/* Employee Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employee *
                  </label>
                  <select
                    value={manualEntryForm.employee_id}
                    onChange={(e) => setManualEntryForm(prev => ({...prev, employee_id: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Employee</option>
                    {employees.map(employee => (
                      <option key={employee.id} value={employee.id}>{employee.full_name}</option>
                    ))}
                  </select>
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date *
                  </label>
                  <input
                    type="date"
                    value={manualEntryForm.date}
                    onChange={(e) => setManualEntryForm(prev => ({...prev, date: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Clock In Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Clock In Time *
                  </label>
                  <input
                    type="time"
                    value={manualEntryForm.clock_in_time}
                    onChange={(e) => setManualEntryForm(prev => ({...prev, clock_in_time: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Clock Out Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Clock Out Time
                  </label>
                  <input
                    type="time"
                    value={manualEntryForm.clock_out_time}
                    onChange={(e) => setManualEntryForm(prev => ({...prev, clock_out_time: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave blank if employee is still working</p>
                </div>

                {/* Reason */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason *
                  </label>
                  <textarea
                    value={manualEntryForm.reason}
                    onChange={(e) => setManualEntryForm(prev => ({...prev, reason: e.target.value}))}
                    rows="4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Explain why this manual entry is needed..."
                  />
                </div>
              </div>
            </div>
            
            {/* Sticky Footer */}
            <div className="flex justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowManualEntryModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleManualEntry}
                disabled={loading || !manualEntryForm.employee_id || !manualEntryForm.date || !manualEntryForm.clock_in_time || !manualEntryForm.reason}
                className="px-4 py-2 text-sm font-medium text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ 
                  backgroundColor: loading || !manualEntryForm.employee_id || !manualEntryForm.date || !manualEntryForm.clock_in_time || !manualEntryForm.reason ? '#9ca3af' : themeColor
                }}
                onMouseEnter={(e) => {
                  if (!loading && manualEntryForm.employee_id && manualEntryForm.date && manualEntryForm.clock_in_time && manualEntryForm.reason) {
                    e.target.style.backgroundColor = '#1e9bb8';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading && manualEntryForm.employee_id && manualEntryForm.date && manualEntryForm.clock_in_time && manualEntryForm.reason) {
                    e.target.style.backgroundColor = themeColor;
                  }
                }}
              >
                {loading ? 'Adding...' : 'Add Entry'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedTimeEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Delete Time Entry</h3>
                <button
                  onClick={closeDeleteModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-600 mb-4">
                Are you sure you want to delete this time entry for <strong>{selectedTimeEntry.employeeName}</strong>? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={closeDeleteModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteTimeEntry}
                  disabled={loading}
                  className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimesheetManagement;